import { RefObject, useEffect, useRef, useState } from "react";
import debounce from "@mui/utils/debounce";

type UseEqualItemWidthsOptions = {
  selector: string;
  containerRef?: RefObject<HTMLElement | null>;
};

type UseEqualItemWidthsResult<T extends HTMLElement = HTMLElement> = {
  containerRef: RefObject<T | null>;
  calculatedWidth: number | null;
};

export function useEqualItemWidths<T extends HTMLElement = HTMLElement>(
  props: UseEqualItemWidthsOptions,
): UseEqualItemWidthsResult<T> {
  const { selector, containerRef: externalRef } = props;

  // Internal ref if caller does not provide one
  const internalRef = useRef<T | null>(null);
  const containerRef = (externalRef as RefObject<T | null>) ?? internalRef;

  const [calculatedWidth, setCalculatedWidth] = useState<number | null>(null);
  const lastWidthRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measureAndApply = () => {
      const items = container.querySelectorAll<HTMLElement>(selector);
      if (!items.length) return;

      // Reset widths before measuring
      items.forEach((el) => {
        el.style.width = "auto";
      });

      let maxWidth = 0;
      items.forEach((el) => {
        maxWidth = Math.max(maxWidth, el.offsetWidth);
      });

      if (maxWidth <= 0) return;

      // Apply equal width
      items.forEach((el) => {
        el.style.width = `${maxWidth}px`;
      });

      // Update state only if value actually changed
      if (lastWidthRef.current !== maxWidth) {
        lastWidthRef.current = maxWidth;
        setCalculatedWidth(maxWidth);
      }
    };

    const measureRAF = () => {
      window.requestAnimationFrame(measureAndApply);
    };

    const debounced = debounce(measureRAF, 100);

    // Initial measure
    measureRAF();

    const ro = new ResizeObserver(() => {
      debounced();
    });
    ro.observe(container);

    window.addEventListener("resize", debounced);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", debounced);
      debounced.clear();
    };
  }, [selector, containerRef]);

  return { containerRef, calculatedWidth };
}
